import { Button, Image, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './style';

export function CardIsleWidget({ page, selectedCard, updateCard }) {
  const { t } = useTranslation();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://s3.amazonaws.com/cardisle.web/static/ecommerce/shop_embed.js';
    script.async = true;
    document.body.appendChild(script);
    window.addEventListener('message', receiveMessage, false);

    return () => {
      window.removeEventListener('message', receiveMessage, false);
    };
  }, [window.initiate_card_isle_widget]);

  window.open_cardisle_iframe_wrapper = e => {
    if (e.target.id === 'edit-card-button') {
      window.initiate_card_isle_widget(
        true,
        'https://www.cardisle.com/shop/#/personalize?sku=' + selectedCard?.pickup_code
      );
      window.open_cardisle_iframe();
    }
    if (e.target.id === 'change-card-button' || e.target.id === 'select-card-button') {
      window.initiate_card_isle_widget(true, 'https://www.cardisle.com/shop/#/selection');
      window.open_cardisle_iframe();
    }
  };

  const receiveMessage = event => {
    if (event.origin !== 'https://www.cardisle.com') return;

    if (event.data.status === 'success') {
      updateCard({
        card_id: event.data.card_id,
        card_image: event.data.card_image,
        card_name: event.data.card_name,
        card_text: event.data.card_text,
        occasion: event.data.occasion_description,
        orientation: event.data.orientation,
        pickup_code: event.data.pickup_code,
        inside_text: event.data.inside_text
      });
    }

    if (event.data === 'close iframe') {
      if (document.getElementById('select-card-button'))
        document.getElementById('select-card-button').textContent = t('selectCard');
      if (document.getElementById('edit-card-button'))
        document.getElementById('edit-card-button').textContent = t('editCard');
      if (document.getElementById('change-card-button'))
        document.getElementById('change-card-button').textContent = t('changeCard');
    }
  };

  const components = useMemo(() => {
    const SelectButton = () => (
      <Button
        color="secondary"
        id="select-card-button"
        onClick={window.open_cardisle_iframe_wrapper}
        variant="outlined"
        size="sm"
        className="card-isle-button"
      >
        {t('selectCard')}
      </Button>
    );
    const CardIsleButtons = ({ style }) => (
      <S.CardIsleButtonsWrapper style={style}>
        <Button
          id="edit-card-button"
          onClick={window.open_cardisle_iframe_wrapper}
          className="card-isle-button"
          color="secondary"
          variant="outlined"
          size="sm"
        >
          {t('editCard')}
        </Button>
        <Button
          id="change-card-button"
          onClick={window.open_cardisle_iframe_wrapper}
          color="secondary"
          variant="link"
          className="card-isle-button"
        >
          {t('changeCard')}
        </Button>
      </S.CardIsleButtonsWrapper>
    );

    return {
      experienceStep: selectedCard ? (
        <>
          <Image src={selectedCard.card_image} width={100} style={{ objectFit: 'contain' }} />
          <CardIsleButtons style={{ flexDirection: 'column', alignItems: 'center', gap: 8 }} />
        </>
      ) : (
        <S.CardIslePreviewWrapper>
          <Image name="greetingCardExperience" width={100} />
          <SelectButton />
        </S.CardIslePreviewWrapper>
      ),
      experienceReview: selectedCard ? (
        <S.CardIslePreviewWrapper>
          <Image src={selectedCard.card_image} width={100} style={{ objectFit: 'contain' }} />
          <S.CardIslePreviewColumn>
            <Typography variant="body2">{selectedCard.card_name}</Typography>
            <Typography variant="body2">
              {t('cardID')}: {selectedCard.card_id}
            </Typography>
            <Typography variant="body2Italic">“{selectedCard.card_text}“</Typography>
            <CardIsleButtons />
          </S.CardIslePreviewColumn>
        </S.CardIslePreviewWrapper>
      ) : (
        <S.CardIslePreviewWrapper>
          <Image name="greetingCardExperience" width={100} />
          <SelectButton />
        </S.CardIslePreviewWrapper>
      )
    };
  }, [selectedCard]);

  return components[page];
}

CardIsleWidget.propTypes = {
  page: PropTypes.string.isRequired,
  selectedCard: PropTypes.shape({
    card_id: PropTypes.string,
    card_image: PropTypes.string,
    card_text: PropTypes.string,
    pickup_code: PropTypes.string
  }),
  updateCard: PropTypes.func.isRequired
};
